import React from 'react'

import { Button, useModalContext } from '@fourthwall/components'
import { PencilIcon, PlusIcon, TrashIcon } from '@fourthwall/icons'

import type { App, AppSection } from '../../types'
import { EditSectionsModal } from '../EditSectionsModal/EditSectionsModal'
import * as S from './SectionsTab.styled'
import { useCreatePlatformAppSection } from './hooks/useCreatePlatformAppSection'
import { useDeletePlatformAppSection } from './hooks/useDeletePlatformAppSection'
import { useUpdatePlatformAppSection } from './hooks/useUpdatePlatformAppSection'

type SectionsTabProps = {
  app: App
}

export const SectionsTab = ({ app }: SectionsTabProps) => {
  const { open } = useModalContext()

  const { createPlatformAppSection, isLoading: isCreatingSection } = useCreatePlatformAppSection()
  const { updatePlatformAppSection, isLoading: isUpdatingSection } = useUpdatePlatformAppSection()
  const { deletePlatformAppSection, isLoading: isDeletingSection } = useDeletePlatformAppSection()

  const isLoading = isCreatingSection || isUpdatingSection || isDeletingSection

  const handleAddSection = (section: AppSection) => {
    open(EditSectionsModal, {
      title: 'Add Section',
      section,
      hmac: app.hmacKey,
      onSave: (newSection: AppSection) => {
        createPlatformAppSection([{ appId: app.id }, newSection])
      },
    })
  }

  const handleEditSection = (section: AppSection) => {
    open(EditSectionsModal, {
      title: 'Edit Section',
      section,
      hmac: app.hmacKey,
      onSave: (updatedSection: AppSection) => {
        updatePlatformAppSection([{ appId: app.id, sectionId: section.id }, updatedSection])
      },
    })
  }

  const handleDeleteSection = (section: AppSection) => {
    deletePlatformAppSection([{ appId: app.id, sectionId: section.id }])
  }

  const renderSettingsInfo = (section: AppSection) => {
    if (!section.settings || section.settings.length === 0) {
      return <S.SectionSettingsInfo>No settings configured</S.SectionSettingsInfo>
    }

    return (
      <S.SectionSettingsInfo>
        {section.settings.length} setting{section.settings.length !== 1 ? 's' : ''} configured
      </S.SectionSettingsInfo>
    )
  }

  return (
    <S.Container>
      <S.Description>
        App sections allow you to add custom pages and functionality to your app. Each section can
        be embedded as an iframe within your app and can have customizable settings.
      </S.Description>

      {app.sections.length > 0 ? (
        <S.SectionsList>
          {app.sections.map((section) => (
            <S.SectionCard key={section.id}>
              <S.SectionInfo>
                <S.SectionName>{section.name}</S.SectionName>
                {section.description && (
                  <S.SectionDescription>{section.description}</S.SectionDescription>
                )}
                <S.SectionUrl>{section.iframeUrl}</S.SectionUrl>
                {renderSettingsInfo(section)}
              </S.SectionInfo>
              <S.SectionActions>
                <Button
                  label="Edit"
                  appearance="secondary"
                  size="small"
                  leftIcon={<PencilIcon height={12} />}
                  onClick={() => handleEditSection(section)}
                />
                <Button
                  label="Delete"
                  appearance="secondary"
                  size="small"
                  leftIcon={<TrashIcon height={12} />}
                  onClick={() => handleDeleteSection(section)}
                />
              </S.SectionActions>
            </S.SectionCard>
          ))}
        </S.SectionsList>
      ) : (
        <S.PlaceholderContainer>
          <S.PlaceholderText>No sections added yet</S.PlaceholderText>
          <S.PlaceholderSubtext>
            Add custom pages and functionality to your app by creating sections.
          </S.PlaceholderSubtext>
        </S.PlaceholderContainer>
      )}

      <S.ButtonContainer>
        <Button
          label="Add Section"
          appearance="primary"
          leftIcon={<PlusIcon />}
          onClick={() =>
            handleAddSection({
              id: '',
              name: '',
              description: '',
              iframeUrl: '',
              settings: [],
            })
          }
          disabled={isLoading}
        />
      </S.ButtonContainer>
    </S.Container>
  )
}
